import { POST_STATUSES } from '@jebel/constants';

import { useCurrentUserCommunityMembership } from 'features/community/hooks';
import { GroupPostCreateInput, useGroupPostCreateMutation } from 'shared/graphql';
import { useCurrentUser, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

/**
 * Create a new `GroupPost` instance.
 *
 * @param groupId - The ID of the group to create the post in.
 * @returns The `create` function and the `loading` state.
 */
export function useGroupPostCreate(groupId: string) {
  const { userId } = useCurrentUser();
  const { showError, showSuccess } = useToast();
  const { data: membership } = useCurrentUserCommunityMembership();

  const [mutate, { loading }] = useGroupPostCreateMutation({
    refetchQueries: ['GroupsList', 'HomeFeedItems'],
  });

  const create = async (data: GroupPostCreateInput) => {
    if (!userId || !groupId) {
      return;
    }

    try {
      const response = await mutate({
        variables: {
          data: {
            // https://8base-dev.atlassian.net/browse/JEB-1553
            status: POST_STATUSES.pending,
            group: { connect: { id: groupId } },
            author: { connect: { id: userId } },
            // https://github.com/jebelapp/jebel/issues/1842
            authorAffiliation: membership?.affiliation,

            ...data,
          },
        },
      });

      showSuccess('Your post has been created successfully.');
      return response;
    } catch (err) {
      recordError(err);
      showError('Something went wrong creating your post, please try again.');

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  return {
    create,
    loading,
  };
}
